/**
 * commerce events should start with `tracking:commerce:`
 *
 * non-commerce events should start with `tracking:activity:`
 */
export enum TrackingEvents {
  // Commerce events
  TrackingCartOpen = "tracking:commerce:view_cart",
  TrackingCartQuantityAdd = "tracking:commerce:add_to_cart",
  TrackingCartQuantityRemove = "tracking:commerce:remove_from_cart",
  TrackingCheckoutAddPaymentInfo = "tracking:commerce:add_payment_info",
  TrackingCheckoutAddShippingInfo = "tracking:commerce:add_shipping_info",
  TrackingCheckoutBeginCheckout = "tracking:commerce:begin_checkout",
  TrackingCheckoutProgress = "tracking:commerce:checkout_progress",
  TrackingMyOrdersReorder = "tracking:commerce:re_order",
  TrackingOrderSummaryPurchase = "tracking:commerce:purchase",
  TrackingOrderTradeIn = "tracking:commerce:trade_in",
  TrackingSelectItem = "tracking:commerce:select_item",
  TrackingViewItem = "tracking:commerce:view_item",
  TrackingViewItemList = "tracking:commerce:view_item_list",

  // Subscription events
  TrackingMySubscriptionPause = "tracking:commerce:pause_subscription",
  TrackingMySubscriptionResume = "tracking:commerce:resume_subscription",
  TrackingMySubscriptionStop = "tracking:commerce:stop_subscription",
  TrackingMySubscriptionAddItem = "tracking:commerce:add_to_subscription",
  TrackingMySubscriptionRemoveItem = "tracking:commerce:remove_item",
  TrackingMySubscriptionDecreaseCarton = "tracking:commerce:decrease_carton",
  TrackingMySubscriptionIncreaseCarton = "tracking:commerce:increase_carton",

  // Non-commerce events
  TrackingCheckoutPageView = "tracking:activity:checkout:pageView",
  TrackingCodeSubmitSuccess = "tracking:activity:code:submit:success",
  TrackingCreateAccountClick = "tracking:activity:createaccount:click",
  TrackingCtaClick = "tracking:activity:cta:click",
  TrackingCheckoutOrderPaymentCtaClick = "tracking:activity:cta:click:checkout:order:payment",
  TrackingEvent = "tracking:activity:event",
  TrackingFaqLink = "tracking:activity:faq:link",
  TrackingFaqSearchClick = "tracking:activity:faq:searchClick",
  TrackingFaqSearchResultClick = "tracking:activity:faq:searchResultClick",
  TrackingFlavorFinderProgress = "tracking:activity:flavorfinder:progress",
  TrackingFlavorFinderResult = "tracking:activity:flavorfinder:result",
  TrackingFreeTrialStart = "tracking:activity:freetrial:start",
  TrackingFollow = "tracking:activity:follow",
  TrackingHardAgeVerificationStart = "tracking:activity:hardageverification:start",
  TrackingHardAgeVerificationSuccess = "tracking:activity:hardageverification:success",
  TrackingListClick = "tracking:activity:viewport:ListClick",
  TrackingLoginClick = "tracking:activity:login:click",
  TrackingLoginKakao = "tracking:activity:login:kakao",
  TrackingLoginFail = "tracking:activity:login:fail",
  TrackingLoginSuccess = "tracking:activity:login:success",
  TrackingLogoutSuccess = "tracking:activity:logout:success",
  TrackingPointsHistoryClick = "tracking:activity:pointsHistory:click",
  TrackingRedeemCheckout = "tracking:activity:redeem:checkout",
  TrackingRedeemClick = "tracking:activity:redeem:click",
  TrackingRewardDiscoverMoreClick = "tracking:activity:redeem:discoverMoreClick",
  TrackingLotteryClick = "tracking:activity:lottery:click",
  TrackingReferralCodeCopyButtonClick = "tracking:activity:referralcodecopybutton:click",
  TrackingReferralCodeInserted = "tracking:activity:referralcodeinserted",
  TrackingShopFilterInteraction = "tracking:activity:shop:filterinteraction",
  TrackingSignUpProgress = "tracking:activity:registration:singupProgress",
  TrackingSignUpRegular = "tracking:activity:registration:signupRegular",
  TrackingSocialIconClick = "tracking:activity:socialicon:click",
  TrackingUnsubscribe = "tracking:activity:unsubscribe:success",
  TrackingUpdateGtmPageName = "tracking:activity:updateGtmPageName",
  TrackingUserAccountDeviceDeregister = "tracking:activity:userAccount:deviceDeregister",
  TrackingUserAccountDeviceRegister = "tracking:activity:userAccount:deviceRegister",
  TrackingUserLoginDeviceRegister = "tracking:activity:userLogin:deviceRegister",
  VideoClick = "tracking:activity:video:click",
  VideoStop = "tracking:activity:video:stop",
  VideoComplete = "tracking:activity:video:complete",
  VideoProgress = "tracking:activity:video:progress",
  TrackingContactUsClick = "tracking:activity:contactUs:click",
  TrackingContactUsPhoneClick = "tracking:activity:contactUsPhone:click",
  TrackingBlogArticleClick = "tracking:activity:article:click",
  TrackingStoreLocatorClick = "tracking:activity:storeLocator:click",
  TrackingSubscribeBlockClick = "tracking:activity:subscribeBlock:click",
  TrackingMenuNavigationClick = "tracking:activity:menuNavigation:click",
  TrackingMenuNavigationListClick = "tracking:activity:menuNavigationList:click",
  TrackingCarouselClick = "tracking:activity:carousel:click",
  TrackingAgeVerificationClick = "tracking:activity:age:verification:click",
  TrackingSurveyStart = "tracking:activity:survey:start",
  TrackingSurveyProgress = "tracking:activity:survey:progress",
  TrackingSurveyCompletion = "tracking:activity:survey:completion",
  TrackingPageView = "tracking:activity:pageView",
  TrackingPopinClose = "tracking:activity:popin:close",
  TrackingPopinLinkClick = "tracking:activity:popin:link:click",
  TrackingPopinView = "tracking:activity:popin:view",
  TrackingQuizStart = "tracking:activity:quiz:start",
  TrackingQuizEnd = "tracking:activity:quiz:end",
  TrackingCrossSellPopupImpression = "tracking:activity:crossSellPopup:impression",
}

export type TrackingEventsAdditionalData = {
  [TrackingEvents.TrackingCtaClick]: {
    cta_name: string;
    cta_location: string;
    section?: string;
  };
  [TrackingEvents.TrackingCheckoutOrderPaymentCtaClick]: CheckoutPayForOrderEventData;
  [TrackingEvents.TrackingEvent]: { event: "contact_form_submit" };
  [TrackingEvents.TrackingFaqLink]: { faq_name: string };
  [TrackingEvents.TrackingHardAgeVerificationStart]: undefined;
  [TrackingEvents.TrackingHardAgeVerificationSuccess]: { hav_method: string };
  [TrackingEvents.TrackingLoginClick]: {
    section: string;
    template_name: string;
  };
  [TrackingEvents.TrackingLoginFail]: { method: "Email" };
  [TrackingEvents.TrackingLoginSuccess]: { user_id: string; method: "Email" | "Phone" };
  [TrackingEvents.TrackingLoginKakao]: undefined;
  [TrackingEvents.TrackingLogoutSuccess]: { user_id: string };
  [TrackingEvents.TrackingReferralCodeCopyButtonClick]: undefined;
  [TrackingEvents.TrackingReferralCodeInserted]: undefined;
  [TrackingEvents.TrackingSignUpProgress]: {
    sign_up_step_name: string;
    sign_up_step_number: number;
    from_kakao: 0 | 1; // required data is 1 or 0 for boolean value
    use_referral_code: "yes" | "no"; // required data is 'yes' or 'no' for string value
  };
  [TrackingEvents.TrackingSignUpRegular]: { from_kakao: 0 | 1; user_id?: string | null }; // required data for from_kakao is 1 or 0 for boolean value;
  [TrackingEvents.TrackingSocialIconClick]: { social_network: string };
  [TrackingEvents.TrackingFollow]: { social_network: string };
  [TrackingEvents.TrackingUserAccountDeviceDeregister]: { device_reference: string; cta_name: string };
  [TrackingEvents.TrackingUserAccountDeviceRegister]: { device_reference: string; cta_name: string };
  [TrackingEvents.TrackingUserLoginDeviceRegister]: { device_reference: string; cta_name: string };
  [TrackingEvents.VideoClick]: { video_title: string };
  [TrackingEvents.VideoStop]: { video_title: string; video_completion: number; video_watchtime: number };
  [TrackingEvents.VideoComplete]: { video_title: string; video_watchtime: number; video_percent: number };
  [TrackingEvents.VideoProgress]: { video_title: string; video_watchtime: number; video_percent: number };
  [TrackingEvents.TrackingContactUsClick]: undefined;
  [TrackingEvents.TrackingContactUsPhoneClick]: undefined;
  [TrackingEvents.TrackingPointsHistoryClick]: { cta_name: string };
  [TrackingEvents.TrackingCheckoutPageView]: SharedData;
  [TrackingEvents.TrackingCheckoutBeginCheckout]: Omit<SharedData<BeginCheckoutEventData>, "checkout_type">;
  [TrackingEvents.TrackingCheckoutProgress]: SharedData<CheckoutProgressEventData>;
  [TrackingEvents.TrackingCheckoutAddPaymentInfo]: SharedData<AddPaymentInfoEventData>;
  [TrackingEvents.TrackingCheckoutAddShippingInfo]: Omit<SharedData<AddShippingInfoEventData>, "checkout_type">;
  [TrackingEvents.TrackingOrderSummaryPurchase]: SharedData<PurchaseEventData>;
  [TrackingEvents.TrackingOrderTradeIn]: SharedData<CommerceEventBaseData>;
  [TrackingEvents.TrackingCartOpen]: CommerceEventBaseData;
  [TrackingEvents.TrackingCartQuantityAdd]: CommerceEventBaseData;
  [TrackingEvents.TrackingCartQuantityRemove]: CommerceEventBaseData;
  [TrackingEvents.TrackingSelectItem]: CommerceEventBaseData;
  [TrackingEvents.TrackingViewItem]: Omit<CommerceEventBaseData, "currency">;
  [TrackingEvents.TrackingViewItemList]: Omit<CommerceEventBaseData, "value">;

  [TrackingEvents.TrackingMySubscriptionPause]: undefined;
  [TrackingEvents.TrackingMySubscriptionResume]: undefined;
  [TrackingEvents.TrackingMySubscriptionStop]: { cancel_reason: string };
  [TrackingEvents.TrackingMySubscriptionAddItem]: {
    added_price: number;
    ecommerce: {
      items: {
        carton_format: string;
        carton_quantity: number;
      }[];
    };
  };
  [TrackingEvents.TrackingMySubscriptionRemoveItem]: SubscriptionUpdateEventBasicData;
  [TrackingEvents.TrackingMySubscriptionIncreaseCarton]: SubscriptionUpdateEventBasicData;
  [TrackingEvents.TrackingMySubscriptionDecreaseCarton]: SubscriptionUpdateEventBasicData;
  [TrackingEvents.TrackingRedeemCheckout]: { reward: string; sensory_experience: boolean };
  [TrackingEvents.TrackingRedeemClick]: { reward: string; sensory_experience: boolean };
  [TrackingEvents.TrackingRewardDiscoverMoreClick]: {
    reward: string;
    reward_uuid: string;
    sensory_experience: boolean;
    cta_name: string;
    cta_location: string;
  };
  [TrackingEvents.TrackingLotteryClick]: undefined;
  [TrackingEvents.TrackingCodeSubmitSuccess]: { user_points_balance: number };
  [TrackingEvents.TrackingFlavorFinderResult]: undefined;
  [TrackingEvents.TrackingFreeTrialStart]: undefined;
  [TrackingEvents.TrackingUnsubscribe]: undefined;
  [TrackingEvents.TrackingFlavorFinderProgress]: {
    flavor_finder_step_name: string;
    flavor_finder_step_number: number;
  };
  [TrackingEvents.TrackingShopFilterInteraction]: { filter_category: string; filter_value: string };
  [TrackingEvents.TrackingListClick]: {
    item_list_name: string;
    item_list_id: string;
  };
  [TrackingEvents.TrackingFaqSearchClick]: { search_term: string };
  [TrackingEvents.TrackingFaqSearchResultClick]: { faq_name: string; search_term: string };
  [TrackingEvents.TrackingBlogArticleClick]: { article_name: string };
  [TrackingEvents.TrackingStoreLocatorClick]: undefined;
  [TrackingEvents.TrackingSubscribeBlockClick]: undefined;
  [TrackingEvents.TrackingMenuNavigationClick]: { menu_link: string };
  [TrackingEvents.TrackingMenuNavigationListClick]: undefined;
  [TrackingEvents.TrackingCarouselClick]: undefined;
  [TrackingEvents.TrackingCreateAccountClick]: undefined;
  [TrackingEvents.TrackingAgeVerificationClick]: {
    age_confirmation: string;
  };
  [TrackingEvents.TrackingSurveyStart]: { survey_question: string; survey_answer: string };
  [TrackingEvents.TrackingSurveyProgress]: {
    survey_question: string;
    survey_answer: string;
    survey_step_number: string;
  };
  [TrackingEvents.TrackingSurveyCompletion]: undefined;
  [TrackingEvents.TrackingPageView]: {
    section: string;
  };
  [TrackingEvents.TrackingPopinClose]: {
    close_reason: "outside_click" | "x_button" | "cta_click";
    popin_location: string;
    popin_name: string;
  };
  [TrackingEvents.TrackingPopinLinkClick]: {
    link_text: string;
    link_url: string;
    popin_location: string;
    popin_name: string;
  };
  [TrackingEvents.TrackingPopinView]: {
    acknowledgement_status: "acknowledged" | "not acknowledged";
    popin_location: string;
    popin_name: string;
  };
  [TrackingEvents.TrackingQuizStart]: undefined;
  [TrackingEvents.TrackingQuizEnd]: { points_earned: number };
  [TrackingEvents.TrackingCrossSellPopupImpression]: { rule_id: string; offer_sku: string[] };
};

export type CheckoutTemplateName = "checkout" | "free-trial-checkout";
export type TemplateName = CheckoutTemplateName | "cart" | "order-confirmation" | "free-trial-order-confirmation-page";
export type SharedData<T = object> = {
  section: TemplateName;
  template_name: TemplateName;
  checkout_type: TemplateName;
} & T;
/**
 * @deprecated
 */

export type SelectedItem = {
  sku: string;
  id: string;
  name: string;
  stock_status: string | null;
  flavor: number;
  strength: number;
  brand: string;
  color: string;
  color_label: string | null;
  price: { currencyCode: string | null; value: number | null };
  categories: [];
  price_tiers: [];
  price_range: null;
  quantity: number;
};

export type CommerceEventItem = {
  currency: string;
  discount: number | "N/A";
  index: number;
  item_brand: string;
  item_category: string;
  item_category2: string;
  item_color: string;
  item_coupon: string;
  item_coupon_type: string;
  item_flavor: string;
  item_id: string;
  item_list_id: string;
  item_list_name: string;
  item_name: string;
  item_sku: string;
  item_strength: string;
  price: number | "N/A";
  quantity: number;
  item_rewards: string;
  purchase_type: string;
  carton_format: string;
  carton_quantity: number | "N/A";
};

export type CommerceEventBaseData = {
  currency: string;
  value: number | "N/A"; // N/A is a special escape for the Flavor Finder Result where we don't have any real number value.
  ecommerce: {
    items: CommerceEventItem[];
  };
};

export type SubscriptionUpdateEventBasicData = {
  ecommerce: {
    items: {
      item_name: string;
    }[];
  };
};

export type CheckoutProgressEventData = {
  checkout_step_name: string;
  checkout_step_number: number;
  checkout_type: string;
} & CommerceEventBaseData;

export type CheckoutGTMFlowType = "fast_flow" | "semi_fast_flow" | "standard_flow";

export type BeginCheckoutEventData = CommerceEventBaseData & {
  checkout_flow_type?: CheckoutGTMFlowType;
};

export type CheckoutPayForOrderEventData = {
  cta_name: "payment";
  cta_location: "checkout";
  checkout_flow_type?: CheckoutGTMFlowType;
};

export type PurchaseEventData = Omit<CommerceEventBaseData, "ecommerce"> & {
  ecommerce: CommerceEventBaseData["ecommerce"] & {
    coupon: string;
    shipping: string;
    tax: string;
    transaction_id: string;
    value: number | "N/A";
  };
  checkout_flow_type?: CheckoutGTMFlowType;
};

export type AddPaymentInfoEventData = {
  payment_type: string;
  payment_info_prefilled?: boolean;
  payment_info_edited?: boolean;
  checkout_flow_type?: CheckoutGTMFlowType;
} & CommerceEventBaseData;

export type AddShippingInfoEventData = {
  shipping_tier: string;
  shipping_info_prefilled?: boolean;
  shipping_info_edited?: boolean;
  checkout_flow_type?: CheckoutGTMFlowType;
} & CommerceEventBaseData;

export type ReOrderEventData = Omit<CommerceEventBaseData, "ecommerce"> & {
  ecommerce: CommerceEventBaseData["ecommerce"] & {
    transaction_id: string;
  };
};

type ExtractEventName<Event, Prefix extends string> = Event extends `${Prefix}${infer Name}` ? Name : never;
type ExtractEvent<Event, Prefix extends string> = Event extends `${Prefix}${string}` ? Event : never;

export type TrackingGTMCommerceEventsMap<Prefix extends string = "tracking:commerce:"> = {
  [
    Event in keyof TrackingGTMEventsMap<Prefix> as Event extends ExtractEventName<TrackingEvents, "tracking:commerce:">
      ? Event
      : never
  ]: TrackingGTMEventsMap<Prefix>[Event];
};

export type TrackingGTMEventsMap<Prefix extends string = "tracking:activity:"> = {
  [Event in TrackingEvents as ExtractEventName<Event, Prefix>]: Event extends keyof TrackingEventsAdditionalData
    ? TrackingEventsAdditionalData[Event]
    : never;
};

export type NonCommerceEventsKeys = ExtractEvent<TrackingEvents, "tracking:activity:">;
export interface TrackingEventInit<Additional> extends EventInit {
  detail: { additionalData: Additional };
}
